import React from 'reactn';
import { connect } from 'react-redux';
import Button from '@material-ui/core/Button';
import PropTypes from 'prop-types';
import {
  TextField,
  Box,
  MenuItem,
  withStyles,
  InputBase,
  ButtonBase,
  Paper,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Grid,
  Zoom,
  Slide,
  List,
  ListItem
} from '@material-ui/core';
import Datatable from 'material-table';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import SortIcon from '@material-ui/icons/Sort';
import FilterListIcon from '@material-ui/icons/FilterList';
import CompareIcon from '@material-ui/icons/Compare';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { UserIcon } from '../UserIcon';
import Hoverable from '../hoverable';
import ReactChartkick, { ColumnChart, LineChart } from 'react-chartkick';
import {
  USER_CONTEXT,
  CONTENT_AREA,
  CONTENT_AREAS_ENUM,
  TOT_DETAILS_ENUM,
  setHousingUnit,
  HOUSING_UNITS,
  getJanusToken,
  setJanusToken
} from '../../constants';
import { Link } from 'react-router-dom';
import { setLocation, LOCATIONS, SELECTED_LOCATION } from '../../constants';
import { gettimeontask, getPermittedLocations, fetchJanusToken } from '../../util/APIUtils';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import TimerIcon from '@material-ui/icons/Timer';
import { CustomChart } from '../chart';
import moment from 'moment';
import { DateRangePicker } from 'materialui-daterange-picker';
import TotDetails from '../tot-details';
import { isContentAreaExempted } from '../users/learner/transcript/utils';
import { setLocation as selectLocation, setHousingUnit as selectHousingUnit } from '../../v2/core/store/.';
import { withRouter } from '../../util/router-compatibility';
import { theme as AdminTheme } from '../../v2/core/containers/theme';
import { CustomTheme } from '@orijinworks/frontend-commons';
import { UnleashService } from '../../v2/services';
import { withUnleash } from '../../v2/core/unleash';

///Styles Constants
const styles = theme => ({
  daysSelectionList: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 16
  },
  daysSelectionItem: {
    width: 'initial',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    height: 40,
    fontSize: 18,
    color: theme.palette.primary.cobalt,
    fontWeight: 'bold',
    '&:first-child': {
      borderTopLeftRadius: 20,
      borderBottomLeftRadius: 20
    },
    '&:last-child': {
      borderTopRightRadius: 20,
      borderBottomRightRadius: 20
    }
  },
  activeDay: {
    backgroundColor: theme.palette.primary.cobalt,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.primary.cobalt
    }
  },
  root: {
    padding: theme.spacing.unit * 2,
    flexGrow: 1
  },
  appBar: {
    background: theme.palette.secondary.main
  },
  timeOnTaskChart: {
    height: 280
  },
  statisticsContainer: {
    flex: 1,
    backgroundColor: theme.palette.colors.purpleMain, 
    margin: 6,
    color: 'white',
    borderRadius: 4
  },
  timeSpentContainer: {
    width: '100%',
    backgroundColor: theme.palette.colors.purpleMain, 
    margin: 6,
    color: 'white',
    borderRadius: 4
  }
});

const ONE_HOUR = 60 * 60 * 1000;

class FacilityUsers extends React.Component {
  _chart = new CustomChart();
  
  // User table columns
  columns = [
    {
      title: 'User Name',
      field: 'name',
      render: rowData => (
        <Hoverable title={rowData.name}>
          <Button
            color="primary"
            onClick={() => {
              this.setGlobal({ selectedUser: rowData.personId, openProfileView: true });
            }}
          >
            <UserIcon style={{ marginRight: 6 }} /> {rowData.name}
          </Button>
        </Hoverable>
      )
    },
    {
      title: 'DOC ID',
      field: 'docid',
      render: rowData => <Hoverable title={rowData.docid}>{rowData.docid}</Hoverable>
    },
    {
      title: 'Current Status',
      field: 'status',
      lookup: { active: 'active', inactive: 'inactive' },
      default: 'active',
      defaultSort: 'asc',
      render: rowData => (
        <Hoverable title={rowData.status}>
          <p style={{ color: rowData.status === 'active' ? 'black' : AdminTheme.palette.text.red }}>{rowData.status}</p>
        </Hoverable>
      )
    },
    {
      title: 'Total Time Spent (hours)',
      field: 'total',
      render: rowData => <Hoverable title={rowData.total.toFixed(2)}>{rowData.total.toFixed(2)}</Hoverable>
    },
    {
      title: 'Education',
      field: 'education',
      render: rowData => <Hoverable title={rowData.education.toFixed(2)}>{rowData.education.toFixed(2)}</Hoverable>
    },
    {
      title: 'Rehabilitation',
      field: 'rehab',
      render: rowData => <Hoverable title={rowData.rehab.toFixed(2)}>{rowData.rehab.toFixed(2)}</Hoverable>
    },
    {
      title: 'Workforce Skills',
      field: 'job',
      render: rowData => <Hoverable title={rowData.job.toFixed(2)}>{rowData.job.toFixed(2)}</Hoverable>
    },
    {
      title: 'Reentry',
      field: 'reentry',
      render: rowData => <Hoverable title={rowData.reentry.toFixed(2)}>{rowData.reentry.toFixed(2)}</Hoverable>
    },
    {
      title: 'Enrichment',
      field: 'enrichment',
      render: rowData => <Hoverable title={rowData.enrichment.toFixed(2)}>{rowData.enrichment.toFixed(2)}</Hoverable>
    },
    {
      title: 'Entertainment',
      field: 'entertainment',
      render: rowData => <Hoverable title={rowData.entertainment.toFixed(2)}>{rowData.entertainment.toFixed(2)}</Hoverable>
    },
    {
      title: 'Miscellaneous',
      field: 'misc',
      render: rowData => <Hoverable title={rowData.misc.toFixed(2)}>{rowData.misc.toFixed(2)}</Hoverable>
    }
  ];
  
  state = {
    dateRangePicker: {
      isOpen: false
    },
    dateRange: 7,
    search: '',
    isLoading: false,
    isCustom: false,
    totDetailsDialog: {
      isOpen: false
    },
    locData: null,
    locationName: null,
    totUserTableData: [],
    allUsers: null,
    totData: null
  };

  componentDidMount() {
    window.scrollTo(0, 0);
    
    if (
      !this.props.selectedLocation ||
      (typeof this.props.selectedLocation === 'object' && Object.keys(this.props.selectedLocation).length === 0)
    ) {
      const params = this.props.match.params;
      const location = {
        locationId: params.locationId.split('&')[0],
        locationName: params.locationName
      };
      this.props.selectLocation(location);
      // Don't call getData here - componentDidUpdate will handle it when selectedLocation prop changes
    } else {
      this.getData();
    }
  }
  
  fetchJanusTokenIfNeeded = async () => {
    const janusToken = getJanusToken();
    if (!janusToken) {
      const selectedLoc = this.props.selectedLocation?.locationId || this.props.match.params.locationId.split('&')[0];
      if (selectedLoc) {
        try {
          const janusObj = await fetchJanusToken(selectedLoc);
          setJanusToken(janusObj.token || '');
        } catch (error) {
          console.error('Failed to fetch Janus token:', error);
        }
      }
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (this.props.selectedLocation && prevProps.selectedLocation !== this.props.selectedLocation) {
      this.changeLocationHandler(this.props.selectedLocation);
    }

    // Only call getData if we don't have data yet and not already loading
    // Also check if selectedLocation was just set (from componentDidMount) or if it actually changed
    const locationChanged = prevProps.selectedLocation?.locationId !== this.props.selectedLocation?.locationId;
    const locationJustSet = !prevProps.selectedLocation && this.props.selectedLocation;
    
    if ((locationChanged || locationJustSet) && !this.state.locData && !this.state.isLoading) {
      this.getData();
    }
  }

  /**
   * @name changeLocationHandler
   * @param {object} event
   * @desc Fires when location change handler is fired.
   * @return {void}
   */
  changeLocationHandler = async loc => {
    this.props.history.push(`/outcomes-and-progress/`);
  };

  getData = async () => {
    const { locData, isLoading } = this.state;
    if (locData || isLoading || LOCATIONS.length === 0) {
      return null;
    }
    
    // Set loading flag immediately to prevent duplicate calls
    this.setState({ isLoading: true });
    
    const selectedLoc = this.props.selectedLocation?.locationId || this.props.match.params.locationId.split('&')[0];
    
    // Safety check - if we still don't have a valid location ID, return early
    if (!selectedLoc) {
      this.setState({ isLoading: false });
      return;
    }
    
    // Ensure Janus token is available before making API calls
    await this.fetchJanusTokenIfNeeded();
    
    // In users mode, only use the selected facility
    const myFacilities = [selectedLoc];
    
    this.setState(prevState => {
      return {
        facilities: myFacilities,
        openFacilityView: false,
        dateRange: this.global.selectedDateRange,
        locationName: this.props.match.params.locationName
      };
    });

    // Check if we have cached data for this date range with user details
    let locationData = this.global.locationDataStore
      .filter(l => {
        return l.locationId === selectedLoc;
      })
      .filter(i => i.dateRange === this.global.selectedDateRange)
      .filter(i => i.hasUserDetails); // Only use cache if it has user details

    var now = Date.now();
    if (locationData.length > 0 && now - locationData[0].timestamp <= ONE_HOUR * 12) {
      this.createTotViewData(locationData[0].data);
      this.setState({
        locData: locationData[0].data
      });
    } else {
      this.fetchTimeonTask(myFacilities);
    }
  };

  fetchTimeonTask = fac => {
    if ([7, 30, 90, 365].indexOf(this.global.selectedDateRange) === -1) {
      this.getTimeonTask(fac, this.global.range, false);
    } else {
      this.getTimeonTask(fac, this.global.selectedDateRange);
    }
  };

  getDateRangeModelFromInteger = range => {
    var d = new Date();
    var _range = range || this.state.dateRange;
    d.setDate(d.getDate() - _range);
    return {
      startDate: d.toISOString(),
      endDate: new Date().toISOString()
    };
  };

  getTimeonTask = (fac, range, isDefiniteRange = true) => {
    let pBody = '';
    var qryDate;
    this.setState({
      locData: null,
      isLoading: true
    });
    let filterDateRange = null;
    if (isDefiniteRange) {
      filterDateRange = this.getDateRangeModelFromInteger(range);
    } else {
      filterDateRange = range;
    }
    this.setGlobal({
      range: filterDateRange
    });
    
    let postBody = {
      requestorId: USER_CONTEXT.personId,
      requestorLocationId: 1697386,
      filterLocationIds: fac,
      filterDateRange,
      retLearnerDetails: true // Always true for users mode
    };

    pBody = JSON.stringify(postBody);
    gettimeontask(postBody).then(data => {
      var facilitiesAverages = [];
      var facilityAve = [];
      var locations = data.locations.map(l => {
        facilityAve = [];
        var name = l.locationName;
        var ave = l.totalTimespent;
        facilityAve.push(name, ave);
        facilitiesAverages.push(facilityAve);
        var contentAreasChart = [];
        var arr = [];
        var _arr = [];
        this.arr = arr;
        l.contentAreas.map(c => {
          _arr = [];
          var name = c.contentAreaName;
          var ave = c.totalTimespent;
          var id = c.contentAreaKey;
          _arr.push(name, ave, id);

          this.arr.push(_arr);
        });
        l.contentAverageChart = arr;
      });
      var facilitiesTotals = this.generateLocationTotals(data.locations);
      data.facilityTotalsChart = facilitiesTotals;
      var facilitiesAverages = this.generateLocationAverages(data.locations);
      data.facilitiesAveragesChart = facilitiesAverages;
      
      // Set contentAverageChart for the first location (for users mode)
      if (data.locations && data.locations.length > 0) {
        data.contentAverageChart = data.locations[0].contentAverageChart || [];
      }
      var details = this.generateSummaryDetails(data.locations);
      data.totalTimespent = details.totalTimespent;
      data.numberOfUsers = details.numberOfUsers;
      
      // Process the data (this will handle user data in users mode)
      this.createTotViewData(data);
      
      this.setState({
        locData: data,
        isLoading: false
      });
      var globalLocationData = {
        locationId: this.props.match.params.locationId,
        dateRange: this.global.selectedDateRange,
        data: data,
        timestamp: Date.now(),
        hasUserDetails: true // Mark this cache entry as having user details
      };
      var _globalDataStore = this.global.locationDataStore;
      _globalDataStore.push(globalLocationData);
      this.setGlobal({
        locationDataStore: _globalDataStore
      });
    });
  };

  createTotViewData(data) {
    var facilitiesAverages = [];
    var facilityAve = [];
    var locations = data.locations.map(l => {
      facilityAve = [];
      var name = l.locationName;
      var ave = l.totalTimespent;
      facilityAve.push(name, ave);
      facilitiesAverages.push(facilityAve);
      var contentAreasChart = [];
      var arr = [];
      var _arr = [];
      this.arr = arr;
      l.contentAreas.map(c => {
        _arr = [];
        var name = c.contentAreaName;
        var ave = c.totalTimespent;
        var id = c.contentAreaKey;
        _arr.push(name, ave, id);

        this.arr.push(_arr);
      });
      l.contentAverageChart = arr;
      
      if (l.userDetails) {
        this.genTableData(l.userDetails);
        this.genUserChart(l.userDetails);
      }
    });
    var facilitiesTotals = this.generateLocationTotals(data.locations);
    data.facilityTotalsChart = facilitiesTotals;
    var facilitiesAverages = this.generateLocationAverages(data.locations);
    data.facilitiesAveragesChart = facilitiesAverages;
    var details = this.generateSummaryDetails(data.locations);
    data.totalTimespent = details.totalTimespent;
    data.numberOfUsers = details.numberOfUsers;
  }

  generateSummaryDetails = loc => {
    var totalTimespent = 0;
    var numOfUsers = 0;
    var details = {};
    loc.map(l => {
      totalTimespent += l.totalTimespent;
      numOfUsers += l.numberOfUsers;
    });
    details.totalTimespent = totalTimespent.toFixed(2);
    details.numberOfUsers = numOfUsers;
    return details;
  };

  generateLocationTotals = loc => {
    var facilitiesTotals = [];
    var facilityTot = [];
    var locations = loc.map(l => {
      facilityTot = [];
      var name = l.locationName;
      var ave = l.totalTimespent;
      facilityTot.push(name, ave);
      facilitiesTotals.push(facilityTot);
      var contentAreasChart = [];
      var arr = [];
      var _arr = [];
      l.contentAreas.map(c => {
        if (isContentAreaExempted(c.contentAreaName)) {
          return;
        }
        _arr = [];
        var name = c.contentAreaName;
        var ave = c.totalTimespent;
        var id = c.contentAreaKey;
        _arr.push(name, ave, id);

        arr.push(_arr);
      });
      l.contentAverageChart = arr;
    });
    return facilitiesTotals;
  };

  generateLocationAverages = loc => {
    var facilitiesAverages = [];
    var numOfLocations = loc.length;
    var edTotals = 0;
    var rehabTotals = 0;
    var jobTotals = 0;
    var reentryTotals = 0;
    var miscTotals = 0;
    var enrichmentTotals = 0;
    var entertainmentTotals = 0;
    var communicationTotals = 0;
    var education = ['Education'];
    var rehab = ['Rehab'];
    var job = ['Job'];
    var reentry = ['Reentry'];
    var enrichment = ['Enrichment'];
    var entertainment = ['Entertainment'];
    var misc = ['Miscellaneous'];

    var locations = loc.map(l => {
      l.contentAreas.map(c => {
        var name = c.contentAreaName;
        var ave = c.averageTimespent;
        if (name === 'Education') {
          edTotals += ave;
        }
        if (name === 'Rehabilitation') {
          rehabTotals += ave;
        }
        if (name === 'Workforce Skills') {
          jobTotals += ave;
        }
        if (name === 'Reentry') {
          reentryTotals += ave;
        }
        if (name === 'Enrichment') {
          enrichmentTotals += ave;
        }
        if (name === 'Entertainment') {
          entertainmentTotals += ave;
        }
        if (name === 'Miscellaneous') {
          miscTotals += ave;
        }
      });
    });

    var edAve = edTotals / numOfLocations;
    var rehabAve = rehabTotals / numOfLocations;
    var jobAve = jobTotals / numOfLocations;
    var reentryAve = reentryTotals / numOfLocations;
    var enrichmentAve = enrichmentTotals / numOfLocations;
    var miscAve = miscTotals / numOfLocations;
    var entAve = entertainmentTotals / numOfLocations;

    education.push(edAve);
    rehab.push(rehabAve);
    job.push(jobAve);
    reentry.push(reentryAve);
    enrichment.push(enrichmentAve);
    entertainment.push(entAve);
    misc.push(miscAve);

    facilitiesAverages.push(education, rehab, job, reentry, enrichment, entertainment, misc);

    return facilitiesAverages;
  };

  // User table methods
  genTableData = (users) => {
    var table = [];
    users.map(u => {
      var _user = {
        personId: u.personId,
        name: u.displayName,
        docid: u.docId,
        id: u.personId,
        enrichment: 0,
        education: 0,
        job: 0,
        misc: 0,
        person_key: 0,
        reentry: 0,
        entertainment: 0,
        rehab: 0,
        total: 0,
        status: u.currentStatusAtFacility
      };
      this._users = _user;
      u.contentAreas.map(c => {
        if (c.contentAreaName === 'Education') {
          this._users.education += c.totalTimespent;
        }
        if (c.contentAreaName === 'Enrichment') {
          this._users.enrichment += c.totalTimespent;
        }
        if (c.contentAreaName === 'Reentry') {
          this._users.reentry += c.totalTimespent;
        }
        if (c.contentAreaName === 'Rehabilitation') {
          this._users.rehab += c.totalTimespent;
        }
        if (c.contentAreaName === 'Miscellaneous') {
          this._users.misc += c.totalTimespent;
        }
        if (c.contentAreaName === 'Workforce Skills') {
          this._users.job += c.totalTimespent;
        }
        if (c.contentAreaName === 'Entertainment') {
          this._users.entertainment += c.totalTimespent;
        }
        this._users.total += c.totalTimespent;
      });
      table.push(_user);
    });
    this.setState({ totUserTableData: table });
  };

  genUserChart = (users) => {
    var chartData = [];
    users.map(u => {
      var user = { name: u.displayName, data: {} };
      u.contentAreas.map(c => {
        if (isContentAreaExempted(c.contentAreaName)) {
          return;
        }
        var timelist = c.timeOnTaskList.map(t => {
          var date = t.date;
          var time = t.timespent;
          user.data[t.date] = t.timespent;
        });
      });
      chartData.push(user);
    });
    this.setState({ allUsers: chartData });
  };

  /**
   * @name handleToTDetailsDialog
   * @param {boolean} isOpen
   * @desc Handles time on task dialog open flag.
   * @return {void}
   */
  handleToTDetailsDialog = isOpen => {
    this.setState({
      totDetailsDialog: {
        ...this.state.totDetailsDialog,
        isOpen
      }
    });
  };

  handleSearchChange = name => event => {
    this.setState({
      [name]: event.target.value
    });
  };

  handleChange = async value => {
    this.setState(prevState => {
      return {
        dateRange: value,
        isCustom: false
      };
    });
    var range = value;
    
    // Ensure Janus token is available before making API calls
    await this.fetchJanusTokenIfNeeded();
    
    // Check if we have cached data for this date range with user details
    let locationData = this.global.locationDataStore
      .filter(l => {
        return l.locationId === this.props.match.params.locationId;
      })
      .filter(i => i.dateRange === range)
      .filter(i => i.hasUserDetails); // Only use cache if it has user details
    
    var now = Date.now();
    if (locationData.length > 0 && now - locationData[0].timestamp <= ONE_HOUR * 12) {
      // Use cached data with user details
      this.createTotViewData(locationData[0].data);
      this.setState({
        locData: locationData[0].data
      });
      this.setGlobal({
        selectedDateRange: range,
        range: this.getDateRangeModelFromInteger(range)
      });
    } else {
      // Fetch fresh data and cache it
      var fac = this.state.facilities;
      this.setGlobal({
        selectedDateRange: range,
        range: this.getDateRangeModelFromInteger(range)
      });
      this.getTimeonTask(fac, range);
    }
  };

  handleDateRangePicker = isOpen => {
    this.setState({
      dateRangePicker: {
        ...this.state.dateRangePicker,
        isOpen
      }
    });
  };

  handleDateRangeChange = async range => {
    range = {
      startDate: moment.utc(range.startDate),
      endDate: moment.utc(range.endDate)
    };
    const daysCount = moment.duration(range.endDate.diff(range.startDate)).asDays();

    const formattedRange = {
      startDate: range.startDate.format(),
      endDate: range.endDate.format()
    };
    this.setGlobal({
      selectedDateRange: daysCount,
      range: formattedRange
    });
    this.setState({
      dateRange: daysCount,
      isCustom: true
    });
    this.handleDateRangePicker(false);
    
    // Ensure Janus token is available before making API calls
    await this.fetchJanusTokenIfNeeded();
    
    // For custom date ranges, always fetch fresh data since they're unique
    var fac = this.state.facilities;
    this.getTimeonTask(
      fac,
      {
        startDate: moment.utc(range.startDate).format(),
        endDate: moment.utc(range.endDate).format()
      },
      false
    );
  };

  render() {
    const { classes } = this.props;

    return (
      <div style={{ padding: '1rem' }}>
        <Box display="flex" position="relative" alignItems="center" justifyContent="center" className={classes.actionBar}>
          <List className={classes.daysSelectionList}>
            <ListItem
              button
              onClick={() => this.handleChange(7)}
              className={[
                classes.daysSelectionItem,
                this.global.selectedDateRange === 7 && !this.state.isCustom ? classes.activeDay : null
              ].join(' ')}
            >
              7 Days
            </ListItem>
            <ListItem
              onClick={() => this.handleChange(30)}
              button
              className={[
                classes.daysSelectionItem,
                this.global.selectedDateRange === 30 && !this.state.isCustom ? classes.activeDay : null
              ].join(' ')}
            >
              30 Days
            </ListItem>
            <ListItem
              onClick={() => this.handleChange(90)}
              button
              className={[
                classes.daysSelectionItem,
                this.global.selectedDateRange === 90 && !this.state.isCustom ? classes.activeDay : null
              ].join(' ')}
            >
              90 Days
            </ListItem>
            <ListItem
              onClick={() => this.handleChange(365)}
              button
              className={[
                classes.daysSelectionItem,
                this.global.selectedDateRange === 365 && !this.state.isCustom ? classes.activeDay : null
              ].join(' ')}
            >
              365 Days
            </ListItem>
            <ListItem
              onClick={() => this.handleDateRangePicker(true)}
              button
              className={[classes.daysSelectionItem, this.state.isCustom ? classes.activeDay : null].join(' ')}
            >
              Custom
            </ListItem>
          </List>
          <Box position="absolute" top="0px">
            <DateRangePicker
              open={this.state.dateRangePicker.isOpen}
              closeOnClickOutside={true}
              maxDate={new Date()}
              toggle={() => this.handleDateRangePicker(!this.state.dateRangePicker.isOpen)}
              onChange={this.handleDateRangeChange}
            />
          </Box>
        </Box>
        <Grid container spacing={24}>
          {this.state.locData ? (
            <Grid item sm={3} xs={12}>
              <div style={{ display: 'flex', flex: 1, flexFlow: 'row wrap' }}>
                <div style={{ flex: 1, display: 'flex', flexFlow: 'row wrap' }}>
                  <Paper style={{ width: '100%', borderRadius: 4, margin: 4 }}>
                    <Typography variant="h6" style={{ margin: 10, color: AdminTheme.palette.text.navy }}>
                      {this.state.locationName}
                    </Typography>
                  </Paper>
                  <Paper className={classes.statisticsContainer}>
                    <LocationOnIcon style={{ width: 18, height: 18, float: 'left', margin: 4 }} />
                    <p style={{ textAlign: 'left', margin: 6, marginTop: 3 }}> areas</p>
                    <Typography variant="h5" style={{ margin: 8, color: 'white', textAlign: 'center' }}>
                      {this.state.locData.locations.length}
                    </Typography>
                  </Paper>
                  <Paper className={classes.statisticsContainer}>
                    <AccountCircleIcon style={{ width: 18, height: 18, float: 'left', margin: 4 }} />
                    <p style={{ textAlign: 'left', margin: 6, marginTop: 3 }}> users</p>
                    <Typography variant="h5" style={{ margin: 8, color: 'white', textAlign: 'center' }}>
                      {this.state.locData.numberOfUsers}
                    </Typography>
                  </Paper>
                  <Paper className={classes.timeSpentContainer}>
                    <TimerIcon style={{ width: 22, height: 22, float: 'left', margin: 4 }} />
                    <p style={{ textAlign: 'left', margin: 6, marginTop: 7 }}> total hours spent</p>
                    <Typography variant="h6" style={{ margin: 10, color: 'white', textAlign: 'center' }}>
                      {this.state.locData.totalTimespent}
                    </Typography>
                  </Paper>
                  <Box width="100%" display="flex" justifyContent="flex-end">
                    <Button onClick={() => this.handleToTDetailsDialog(true)} style={{ width: '100%' }} color="primary">
                      View Time On Task Details
                    </Button>
                    <TotDetails
                      contentAreaVisible
                      heading={`${this.state.locationName} Time on Task`}
                      exportFileName={this.state.locationName}
                      locations={this.state.locData.locations}
                      activeLocationId={this.props.match.params.locationId}
                      open={this.state.totDetailsDialog.isOpen}
                      type={TOT_DETAILS_ENUM.Location}
                      closeHandler={this.handleToTDetailsDialog}
                      locationType="FACILITY"
                    />
                  </Box>
                </div>
              </div>
            </Grid>
          ) : (
            <Paper style={{ padding: '1rem', width: '100%' }}>
              <CircularProgress className={classes.progress} />
              <h1>Loading time on task data...</h1>
            </Paper>
          )}

          <Grid item sm={9} xs={12}>
            {this.state.locData ? (
              <Paper className={classes.timeOnTaskChart}>
                <div>
                  {this.state.totData && this.state.totData.length > 0 && this.state.totData.length < 15 ? (
                    <Slide in unmountOnExit direction="left">
                      <div>
                        <Typography variant="h6" style={{ textAlign: 'center', padding: '.5rem', fontSize: 18 }}>
                          Users Time on Task
                        </Typography>
                        <LineChart
                          library={{
                            ...this._chart.getDefaultOptions,
                            legend: {
                              display: true
                            }
                          }}
                          ytitle="Time Spent (hours)"
                          height={220}
                          stacked
                          adapter="chartjs"
                          data={this.state.totData}
                        />
                      </div>
                    </Slide>
                  ) : (
                    <Zoom in unmountOnExit>
                      <div>
                        <Typography variant="h6" style={{ textAlign: 'center', padding: '.5rem', fontSize: 18 }}>
                          Time on Task by Pathways
                        </Typography>
                        {( this.state.locData.contentAverageChart && this.state.locData.contentAverageChart.length > 0) && (
                          <ColumnChart
                            dataset={{
                              ...this._chart.getDefaultDatasetOptions,
                              backgroundColor: this.state.locData.contentAverageChart.map(data =>
                                CONTENT_AREA[data[2]] ? CONTENT_AREA[data[2]].bgColor : AdminTheme.palette.background.blue
                              )
                            }}
                            library={this._chart.getDefaultOptions}
                            stacked
                            ytitle="Time Spent (hours)"
                            height={220}
                            adapter="chartjs"
                            data={this.state.locData.contentAverageChart}
                          />)}
                      </div>
                    </Zoom>
                  )}
                </div>
              </Paper>
            ) : null}
          </Grid>
        </Grid>

        {/* Users table */}
        {this.state.totUserTableData && this.state.totUserTableData.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <Zoom in unmountOnExit>
              <Paper style={{ marginTop: '2rem' }}>
                <p style={{ margin: '1rem', marginTop: '1rem' }}>
                  Select up to 15 learners to see their time on task details above.
                </p>
                <Datatable
                  title={`${this.state.locationName} Users Time on Task`}
                  data={this.state.totUserTableData}
                  columns={this.columns}
                  icons={{
                    Search: SearchIcon,
                    ResetSearch: CloseIcon,
                    FirstPage: FirstPageIcon,
                    LastPage: LastPageIcon,
                    NextPage: NavigateNextIcon,
                    PreviousPage: NavigateBeforeIcon,
                    Filter: FilterListIcon,
                    Sort: SortIcon,
                    SortArrow: ArrowDownwardIcon,
                    Export: SaveAltIcon,
                    Edit: EditIcon,
                    User: <UserIcon />
                  }}
                  options={{
                    pageSize: 20,
                    selection: true,
                    sorting: true,
                    exportButton: true,
                    exportAllData: true,
                    rowStyle: rowData => ({
                      backgroundColor: rowData.status === 'active' ? '#ffffff' : '#e8e8e8'
                    })
                  }}
                  onSelectionChange={rows => {
                    var usersArr = [];
                    var chartArr = [];
                    var chartData = {};
                    var _chartData = this.state.allUsers;
                    rows.map(r => {
                      usersArr.push(r);
                    });
                    usersArr.map(u => {
                      if (_chartData !== null) {
                        var user = _chartData.filter(o => o.name === u.name);
                        user.map(u => chartArr.push(u));
                      }
                    });
                    this.setState({
                      totData: chartArr
                    });
                  }}
                  actions={[
                    {
                      icon: CompareIcon,
                      onClick: (event, rowData) => {
                        var arr = Object.keys(rowData).map(key => rowData[key]);
                        this.setState({
                          selectedUsers: arr
                        });
                      }
                    }
                  ]}
                />
              </Paper>
            </Zoom>
          </div>
        )}
      </div>
    );
  }
}

FacilityUsers.propTypes = {
  classes: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  return state.app;
};

const mapDispatchToProps = dispatch => {
  return {
    selectLocation: location => dispatch(selectLocation(location)),
    selectHousingUnit: hu => dispatch(selectHousingUnit(hu))
  };
};

export default withUnleash(
  connect(mapStateToProps, mapDispatchToProps)(
    withStyles(styles)(
      withRouter(FacilityUsers)
    )
  ),
  UnleashService.FLAGS.SHOW_FACILITY_DATA_WITH_HOUSING_UNIT_IN_PROGRESS_AND_OVERVIEW
);
